import { createSelector } from 'reselect';

import CART_ITEM from '../constants/cartItems';
import {
  albumPrintPackageRequiresShipping,
  getCheckoutCartItemPrice,
  getParsedRoesConfiguration,
} from './helpers';
import { CURRENCY } from '../../../constants/currency';
import { albumCustomPrintRequiresAddressToConfig } from '../../../util/custom-prints';
import { getPrintPackageCartItemNonConfiguredItems } from '../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/helpers';
import { DIGITAL_DOWNLOADS_MODE } from '../../../constants/print-packages';
import { getPrintPackageSpecialtyPrintsWithoutConfiguration } from '../../../util/print-packages';
import { CUSTOM_PRINT_SUBTYPE } from '../../../constants/pro-custom-prints';

/**
 * Returns the cart.
 *
 * @param {Object} state The global redux state.
 * @returns {Array}
 */
export const getCheckoutCart = state => state.checkout.cart;

/**
 * Returns the previous cart.
 *
 * @param {Object} state The global redux state.
 * @returns {Array}
 */
export const getCheckoutPreviousCart = state => state.checkout.previousCart;

/**
 * Gets the PrintPartnerShippingOption id the customer picked, `null` meaning
 * the print partner's standard shipping.
 *
 * @param {Object} state
 * @returns {string|null}
 */
export const getCheckoutPrintPartnerShippingOptionId = state =>
  state.checkout.printPartnerShippingOptionId;

/**
 * Allows passing a photo & implement it at the selector level
 *
 * @param {Object} state The global redux state.
 * @param {Object} photo the photo to pass down to selectors
 * @returns {Object}
 */
export const selectByPhoto = (state, photo) => photo;

/**
 * Allows passing an identity & implement it at the selector level
 *
 * @param {Object} state The global redux state.
 * @param {Object} identities the identity to pass down to selectors
 * @returns {Object}
 */
export const selectByIdentities = (state, identities) => identities;

/**
 * Calculates the total cart items including quantity.
 */
export const getCheckoutCartTotalItems = createSelector(getCheckoutCart, cart =>
  cart.reduce((sum, item) => {
    if (item.quantity > 0) {
      return sum + item.quantity;
    }

    return sum + 1;
  }, 0),
);

//
// Cart item type getters
//

/**
 * Returns all AlbumCustomPrint cart items.
 */
export const getAlbumCustomPrintCartItems = createSelector(
  getCheckoutCart,
  cart => cart.filter(item => item.type === CART_ITEM.ALBUM_CUSTOM_PRINT),
);

/**
 * Returns all purchased custom print cart items.
 */
const getPurchasedCustomPrintCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.PURCHASED_CUSTOM_PRINT),
);

/**
 * Returns all AlbumFolder subscription cart items.
 */
const getAlbumFolderSubsriptionCartItems = createSelector(
  getCheckoutCart,
  cart =>
    cart.filter(item => item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION),
);

/**
 * Returns all Album subscription cart items.
 */
const getAlbumSubscriptionCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.ALBUM_SUBSCRIPTION),
);

/**
 * Returns all AlbumPrintPackage cart items.
 */
const getAlbumPrintPackageCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.ALBUM_PRINT_PACKAGE),
);

/**
 * Returns all AlbumPrintPackage cart items.
 */
const getPurchasedPrintPackageCartItems = createSelector(
  getCheckoutCart,
  cart => cart.filter(item => item.type === CART_ITEM.PURCHASED_PRINT_PACKAGE),
);

/**
 * Returns all digital download cart items.
 */
const getDigitalDownloadCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.DIGITAL_DOWNLOAD),
);

/**
 * Returns all purchased digital download cart items.
 */
const getPurchasedDigitalDownloadCartItems = createSelector(
  getCheckoutCart,
  cart =>
    cart.filter(item => item.type === CART_ITEM.PURCHASED_DIGITAL_DOWNLOAD),
);

/**
 * Returns all individual print cart items.
 */
const getIndividualPrintCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.INDIVIDUAL_PRINT),
);

/**
 * Returns all legacy print cart items.
 */
const getLegacyPrintCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.LEGACY_PRINT),
);

/**
 * Returns all photo download cart items.
 */
const getPhotoDownloadCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.PHOTO_DOWNLOAD),
);

/**
 * Returns all PrintProduct cart items.
 */
const getPrintProductCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.PRINT_PRODUCT),
);

/**
 * Returns all PhotoPricingPrintProduct cart items.
 */
const getPhotoPricingPrintProductCartItems = createSelector(
  getCheckoutCart,
  cart =>
    cart.filter(item => item.type === CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT),
);

/**
 * Returns all PurchasedPhotoPricingPrintProduct cart items.
 */
const getPurchasedPhotoPricingPrintProductCartItems = createSelector(
  getCheckoutCart,
  cart =>
    cart.filter(
      item => item.type === CART_ITEM.PURCHASED_PHOTO_PRICING_PRINT_PRODUCT,
    ),
);

/**
 * Returns all DigitalBundle cart items.
 */
const getDigitalBundleCartItems = createSelector(getCheckoutCart, cart =>
  cart.filter(item => item.type === CART_ITEM.DIGITAL_BUNDLE),
);

/**
 * Returns all Print Order Service cart items.
 */
const getPrintPartnerOrderServiceCartItems = createSelector(
  getCheckoutCart,
  cart => cart.filter(item => item.type === CART_ITEM.PRINT_ORDER_SERVICE),
);

//
// Checkout mutation / Checkout price query item prep
//

/**
 * Prepares AlbumCustomPrint cart items for checkout / checkout price.
 */
export const getAlbumCustomPrintCheckoutItems = createSelector(
  getAlbumCustomPrintCartItems,
  items =>
    items.map(item => ({
      album_custom_print_id: item.albumCustomPrint.id,
      identity_id: item.identity?.id,
      include_shipping: item.shipping,
      quantity: item.quantity ?? 1,
      is_prepaid: item?.prePaid ?? false,
      configuration: item?.configuration
        ? {
            custom_print_caption:
              item?.configuration?.custom_print_caption ?? undefined,
            photos:
              item?.configuration?.photos?.map?.(photo => {
                return {
                  photo_id: photo.photo?.id,
                  asset_image_id: photo.asset_image?.id,
                  position: photo.position,
                  cropbox: photo.cropbox,
                };
              }) ?? [],
            account_card: item?.configuration?.accountCard
              ? {
                  account_card_id: item.configuration.accountCard.id,
                }
              : undefined,
          }
        : undefined,
    })),
);

/**
 * Prepares PurchasedCustomPrint cart items for checkout / checkout price.
 */
export const getPurchasedCustomPrintCheckoutItems = createSelector(
  getPurchasedCustomPrintCartItems,
  items =>
    items.map(item => ({
      purchased_custom_print_id: item.purchasedCustomPrint.id,
      album_custom_print_id: item.purchasedCustomPrint.album_custom_print.id,
      identity_id: item.identity?.id,
      include_shipping: item.shipping,
      quantity: item.quantity ?? 1,
      configuration: item?.configuration
        ? {
            custom_print_caption:
              item?.configuration?.custom_print_caption ?? undefined,
            photos:
              item?.configuration?.photos?.map?.(photo => {
                return {
                  photo_id: photo.photo?.id,
                  asset_image_id: photo.asset_image?.id,
                  position: photo.position,
                  cropbox: photo.cropbox,
                };
              }) ?? [],
            account_card: item?.configuration?.accountCard
              ? {
                  account_card_id: item.configuration.accountCard.id,
                }
              : undefined,
          }
        : undefined,
    })),
);

/**
 * Prepares AlbumFolder subscription cart items for checkout / checkout price.
 */
export const getAlbumFolderSubscriptionCheckoutItems = createSelector(
  getAlbumFolderSubsriptionCartItems,
  items =>
    items.map(item => ({
      album_folder_id: item.albumFolder.id,
      identity_id: item.identity.id,
    })),
);

/**
 * Prepares Album subscription cart items for checkout / checkout price.
 */
export const getAlbumSubscriptionCheckoutItems = createSelector(
  getAlbumSubscriptionCartItems,
  items =>
    items.map(item => ({
      albums: [item.album.id],
      identity_id: item.identity.id,
    })),
);

/**
 * Prepares AlbumPrintPackage cart items for checkout / checkout price.
 */
export const getAlbumPrintPackageCheckoutItems = createSelector(
  getAlbumPrintPackageCartItems,
  items =>
    items.map(item => {
      const individualPhotoSpecialtyPrints =
        getPrintPackageSpecialtyPrintsWithoutConfiguration(
          item?.albumPrintPackage,
          item?.allowMultiPose,
        );
      const downloadableItems =
        item?.albumPrintPackage?.print_package?.items?.records?.filter(
          printPackageItem =>
            !item.isPrepaid &&
            (!!printPackageItem.digital_bundle ||
              printPackageItem.grants_purchased_identity),
        );
      const { selectedPrintPackageItems = [] } = item;

      return {
        checkout_item_client_id: item.id,
        photo_id: item.photo?.id,
        hierarchy_print_package: item?.albumPrintPackage
          ? {
              hierarchy_print_package_id: item.albumPrintPackage.id,
              extended_print_package_items: [
                ...(downloadableItems ?? [])
                  .filter(
                    downloadableItem =>
                      !selectedPrintPackageItems?.length ||
                      !!selectedPrintPackageItems.find(
                        ({ id }) => id === downloadableItem.id,
                      ),
                  )
                  .map(downloadableItem => ({
                    print_package_item_id: downloadableItem.id,
                  })),
                ...(item.extendedPrintPackageItems ?? []).map(
                  cartPrintItem => ({
                    print_package_item_id: cartPrintItem.printPackageItem.id,
                    account_card_id: cartPrintItem.accountCard?.id,
                    photo_id: cartPrintItem.photo?.id,
                    album_id: item.album.id,
                    digital_download_account_card_id:
                      !!cartPrintItem?.accountCard?.id &&
                      cartPrintItem?.accountCard?.id ===
                        item?.digitalDownloadAccountCard?.id
                        ? cartPrintItem.accountCard.id
                        : undefined,
                    custom_print_configuration:
                      cartPrintItem?.printPackageItem?.custom_print &&
                      cartPrintItem?.printPackageItem?.custom_print?.subtype ===
                        CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
                        ? {
                            photos: [
                              {
                                photo_id: cartPrintItem.photo?.id,
                                position:
                                  cartPrintItem?.printPackageItem?.custom_print
                                    ?.photos_no === 2
                                    ? 1
                                    : 0,
                              },
                              {
                                photo_id:
                                  cartPrintItem?.printPackageItem?.custom_print
                                    ?.photos_no === 2
                                    ? item?.photo?.id
                                    : undefined,
                                position: 0,
                              },
                            ].filter(({ photo_id }) => !!photo_id),
                          }
                        : undefined,
                  }),
                ),
                ...(individualPhotoSpecialtyPrints ?? [])
                  .filter(
                    specialtyPrintItem =>
                      !item.prePaid &&
                      !(item.extendedPrintPackageItems ?? []).find(
                        extendedItem =>
                          extendedItem.printPackageItem.id ===
                          specialtyPrintItem.id,
                      ) &&
                      (!selectedPrintPackageItems?.length ||
                        !!selectedPrintPackageItems.find(
                          ({ id }) => id === specialtyPrintItem.id,
                        )),
                  )
                  .map(specialtyPrintItem => ({
                    print_package_item_id: specialtyPrintItem.id,
                    custom_print_configuration: {
                      photos: [{ photo_id: item.photo?.id, position: 0 }],
                    },
                  })),
                // Did not mix the logic wit extendedPrintPackageItems because they
                // affect directly the UI of the checkout.
                ...(item.configurableItems ?? [])
                  .filter(
                    configurableItem =>
                      !selectedPrintPackageItems?.length ||
                      !!selectedPrintPackageItems.find(
                        ({ id }) => id === configurableItem.printPackageItem.id,
                      ),
                  )
                  .map(configurableItem => {
                    const { accountCard, roesConfiguration, photos } =
                      configurableItem.config ?? {};

                    return {
                      print_package_item_id:
                        configurableItem.printPackageItem.id,
                      custom_print_configuration: photos
                        ? {
                            photos: photos.map(({ photo, position }) => ({
                              photo_id: photo.id,
                              position,
                            })),
                          }
                        : undefined,
                      account_card_id: accountCard?.id,
                      roes_configuration: roesConfiguration
                        ? getParsedRoesConfiguration(roesConfiguration)
                        : undefined,
                      digital_download_account_card_id:
                        !!accountCard?.id &&
                        accountCard?.id === item?.digitalDownloadAccountCard?.id
                          ? accountCard.id
                          : undefined,
                    };
                  }),
              ],
            }
          : undefined,
        digital_downloads_purchase: item.includeDigitalDownload,
        quantity: item.quantity,
        identity_id: item.identity?.id,
        album_id: item.album?.id,
        is_prepaid: item.prePaid ?? false,
      };
    }),
);

/**
 * Prepares AlbumPrintPackage cart items for checkout / checkout price.
 */
export const getPurchasedPrintPackageCheckoutItems = createSelector(
  getPurchasedPrintPackageCartItems,
  items =>
    items.map(item => {
      const individualPhotoSpecialtyPrints =
        getPrintPackageSpecialtyPrintsWithoutConfiguration(
          item?.albumPrintPackage,
          item?.allowMultiPose,
        );
      const downloadableItems =
        item?.albumPrintPackage?.print_package?.items?.records?.filter(
          printPackageItem =>
            !item.isPrepaid &&
            (!!printPackageItem.digital_bundle ||
              printPackageItem.grants_purchased_identity),
        );

      const { selectedPrintPackageItems = [] } = item;

      return {
        checkout_item_client_id: item.id,
        photo_id: item.photo?.id,
        hierarchy_print_package: item?.albumPrintPackage
          ? {
              hierarchy_print_package_id: item.albumPrintPackage.id,
              purchased_print_package_id: item.purchasedPrintPackage.id,
              extended_print_package_items: [
                ...(downloadableItems ?? [])
                  .filter(
                    downloadableItem =>
                      !selectedPrintPackageItems?.length ||
                      !!selectedPrintPackageItems.find(
                        ({ id }) => id === downloadableItem.id,
                      ),
                  )
                  .map(downloadableItem => ({
                    print_package_item_id: downloadableItem.id,
                  })),
                ...(item.configurableItems ?? [])
                  .filter(
                    configurableItem =>
                      !selectedPrintPackageItems?.length ||
                      !!selectedPrintPackageItems.find(
                        ({ id }) => id === configurableItem.printPackageItem.id,
                      ),
                  )
                  .map(configurableItem => {
                    const { accountCard, roesConfiguration, photos } =
                      configurableItem.config ?? {};

                    return {
                      print_package_item_id:
                        configurableItem.printPackageItem.id,
                      account_card_id: accountCard?.id,
                      roes_configuration: roesConfiguration
                        ? getParsedRoesConfiguration(roesConfiguration)
                        : undefined,
                      custom_print_configuration:
                        configurableItem?.printPackageItem?.custom_print
                          ?.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
                          ? {
                              photos: photos
                                .map(({ photo, position }) => ({
                                  photo_id: photo?.id,
                                  position,
                                }))
                                .filter(({ photo_id }) => !!photo_id),
                            }
                          : undefined,
                    };
                  }),
                ...(individualPhotoSpecialtyPrints ?? [])
                  .filter(
                    specialtyPrintItem =>
                      !(item.extendedPrintPackageItems ?? []).find(
                        extendedItem =>
                          extendedItem.printPackageItem.id ===
                          specialtyPrintItem.id,
                      ) &&
                      (!selectedPrintPackageItems?.length ||
                        !!selectedPrintPackageItems.find(
                          ({ id }) => id === specialtyPrintItem.id,
                        )),
                  )
                  .map(specialtyPrintItem => ({
                    print_package_item_id: specialtyPrintItem.id,
                    custom_print_configuration: {
                      photos: [{ photo_id: item.photo?.id, position: 0 }],
                    },
                  })),
              ],
            }
          : undefined,
        digital_downloads_purchase: item.includeDigitalDownload,
        quantity: item.quantity,
        identity_id: item.identity?.id,
        album_id: item.album?.id,
      };
    }),
);

/**
 * Prepares digital download cart items for checkout / checkout price.
 */
export const getDigitalDownloadCheckoutItems = createSelector(
  getDigitalDownloadCartItems,
  items => {
    const checkoutItems = [];

    items.forEach(item => {
      if (item.quantity > 0) {
        for (let i = 0; i < item.quantity; i += 1) {
          checkoutItems.push({
            checkout_item_client_id: item.id,
            photo_id: item.photo?.id,
            album_id: item.album?.id,
            album_folder_id: item.albumFolder?.id,
            digital_downloads_purchase: true,
            identity_id: item.identity.id,
            is_prepaid: item.prePaid ?? false,
          });
        }
      } else {
        checkoutItems.push({
          checkout_item_client_id: item.id,
          photo_id: item.photo?.id,
          album_id: item.album?.id,
          album_folder_id: item.albumFolder?.id,
          digital_downloads_purchase: true,
          identity_id: item.identity.id,
          is_prepaid: item.prePaid ?? false,
        });
      }
    });

    return checkoutItems;
  },
);

/**
 * Prepares purchased digital download cart items for checkout / checkout price.
 */
export const getPurchasedDigitalDownloadCheckoutItems = createSelector(
  getPurchasedDigitalDownloadCartItems,
  items =>
    items.map(item => ({
      checkout_item_client_id: item.id,
      purchased_photo_pricing_id: item.purchasedPhotoPricing.id,
      photo_id: item.photo?.id,
      album_id: item.album?.id,
      digital_downloads_purchase: true,
      identity_id: item.identity.id,
    })),
);

/**
 * Prepares individual print cart items for checkout / checkout price.
 */
export const getIndividualPrintCheckoutItems = createSelector(
  getIndividualPrintCartItems,
  items =>
    items.map(item => ({
      checkout_item_client_id: item.id,
      photo_id: item.photo.id,
      album_id: item.album.id,
      photo_pricing_print_item_id: item.printItem.id,
      quantity: item.quantity,
      print_without_watermark: !item.watermark,
    })),
);

/**
 * Prepares legacy print cart items for checkout / checkout price.
 */
export const getLegacyPrintCheckoutItems = createSelector(
  getLegacyPrintCartItems,
  items =>
    items.map(item => ({
      photo_id: item.photo.id,
      album_id: item.album?.id,
      photo_print_size_id: item.photoPrintSize.id,
      quantity: item.quantity,
      print_without_watermark: !item.watermark,
    })),
);

/**
 * Prepares PrintProduct cart items for checkout / checkout price.
 */
export const getPrintProductCheckoutItems = createSelector(
  getPrintProductCartItems,
  items =>
    items.map(item => ({
      album_custom_print_id: item.albumCustomPrint.id,
      include_shipping: true,
      quantity: item.quantity,
      configuration: {
        photos: [],
        account_card: {
          account_card_id: item.accountCard.id,
        },
      },
    })),
);

/**
 * Prepares PhotoPricingPrintProductCartItems cart items for checkout / checkout price.
 */
export const getPhotoPricingPrintProductCheckoutItems = createSelector(
  getPhotoPricingPrintProductCartItems,
  items =>
    items.map(item => {
      let albumFolderTagPrintProductItem;
      const pricedTag = item.photo?.price_album_folder_tag;
      const { roesConfiguration } = item ?? {};
      if (pricedTag) {
        const relatedPricedTagPrintProduct = (
          pricedTag.print_product_items?.records ?? []
        ).find(
          printProductItem =>
            printProductItem.print_product_size.id ===
            item.photoPricingPrintProduct.print_product_size.id,
        );

        // Safeguard against a 0 value or erroneous setup.
        if (
          relatedPricedTagPrintProduct &&
          relatedPricedTagPrintProduct.price?.value
        ) {
          albumFolderTagPrintProductItem = {
            album_folder_tag_print_product_item_id:
              relatedPricedTagPrintProduct.id,
            account_card_id: item.accountCard?.id,
            roes_configuration: item.roesConfiguration
              ? getParsedRoesConfiguration(roesConfiguration)
              : undefined,
          };
        }
      }

      return {
        photo_pricing_print_product_item: albumFolderTagPrintProductItem
          ? undefined
          : {
              photo_pricing_print_product_item_id:
                item.photoPricingPrintProduct.id,
              account_card_id: item.accountCard?.id,
              roes_configuration: item.roesConfiguration
                ? getParsedRoesConfiguration(roesConfiguration)
                : undefined,
            },
        quantity: item.quantity,
        photo_id: item.photo?.id,
        album_id: item.album?.id,
        album_folder_id: item.albumFolder?.id,
        is_prepaid: item.prePaid,
        album_folder_tag_print_product_item: albumFolderTagPrintProductItem,
        identity_id: item.identity?.id,
      };
    }),
);

export const getPurchasedPhotoPricingPrintProductCheckoutItems = createSelector(
  getPurchasedPhotoPricingPrintProductCartItems,
  items =>
    items.map(item => {
      const { roesConfiguration, purchasedPhotoPricing } = item ?? {};
      const { photo_pricing_print_product_item: printProductItem } =
        purchasedPhotoPricing;

      return {
        purchased_photo_pricing_id: purchasedPhotoPricing.id,
        photo_pricing_print_product_item: {
          photo_pricing_print_product_item_id: printProductItem.id,
          account_card_id: item.accountCard?.id,
          roes_configuration: item.roesConfiguration
            ? getParsedRoesConfiguration(roesConfiguration)
            : undefined,
        },
        quantity: item.quantity,
        photo_id: item.photo?.id,
        album_id: item.album?.id,
        identity_id: item.identity?.id,
      };
    }),
);

export const getDigitalBundleCheckoutItems = createSelector(
  getDigitalBundleCartItems,
  items =>
    items.map(item => {
      const { digitalBundle, identity, photos } = item ?? {};

      return {
        digital_bundle_id: digitalBundle.id,
        identity_id: digitalBundle.is_multi_identity ? undefined : identity.id,
        photo_ids: photos?.length > 0 ? photos.map(({ id }) => id) : undefined,
      };
    }),
);

export const getPrintOrderServiceCheckoutItems = createSelector(
  getPrintPartnerOrderServiceCartItems,
  items =>
    items.map(item => {
      const { printPartnerOrderService } = item ?? {};

      return {
        print_partner_order_service_id: printPartnerOrderService?.id,
      };
    }),
);

/**
 * Prepares photo download items for checkout / checkout price.
 *
 * Do note that Photo Downloads is *not* the same as Digital Downloads.
 * Digital downloads are priced according to the Sell Photos-defined Photo Price.
 * These can also be limited by Print Packages - i.e you may not be able to buy
 * them without buying a print package first.
 *
 * Photo Downloads, on the other hand, use Free Web Gallery Pricing. They're
 * independent from Print Packages, and can be bought even outside of matches.
 */
export const getPhotoDownloadCheckoutItems = createSelector(
  getPhotoDownloadCartItems,
  items =>
    items.map(item => ({
      photo_id: item.photo.id,
      album_id: item.album?.id,
    })),
);

/**
 * Prepares all cart items for checkout / checkout price.
 */
export const getCheckoutCartCheckoutItems = createSelector(
  getAlbumCustomPrintCheckoutItems,
  getPurchasedCustomPrintCheckoutItems,
  getAlbumFolderSubscriptionCheckoutItems,
  getAlbumSubscriptionCheckoutItems,
  getAlbumPrintPackageCheckoutItems,
  getPurchasedPrintPackageCheckoutItems,
  getDigitalDownloadCheckoutItems,
  getPurchasedDigitalDownloadCheckoutItems,
  getIndividualPrintCheckoutItems,
  getLegacyPrintCheckoutItems,
  getPhotoDownloadCheckoutItems,
  getPrintProductCheckoutItems,
  getPhotoPricingPrintProductCheckoutItems,
  getPurchasedPhotoPricingPrintProductCheckoutItems,
  getDigitalBundleCheckoutItems,
  getPrintOrderServiceCheckoutItems,
  (
    albumCustomPrints,
    purchasedCustomPrints,
    albumFolderSubscriptions,
    albumSubscriptions,
    albumPrintPackages,
    purchasedPrintPackages,
    digitalDownloads,
    purchasedDigitalDownloads,
    individualPrintItems,
    legacyPrintItems,
    photoDownloadItems,
    printProductItems,
    photoPricingPrintProductItems,
    purchasedPhotoPricingPrintProductItems,
    digitalBundleItems,
    printOrderServiceItems,
  ) => ({
    custom_print_items: [
      ...albumCustomPrints,
      ...purchasedCustomPrints,
      ...printProductItems,
    ],
    subscription_items: [...albumFolderSubscriptions, ...albumSubscriptions],
    photo_items: [
      ...albumPrintPackages,
      ...purchasedPrintPackages,
      ...digitalDownloads,
      ...purchasedDigitalDownloads,
      ...individualPrintItems,
      ...photoPricingPrintProductItems,
      ...purchasedPhotoPricingPrintProductItems,
    ],
    photo_print_items: legacyPrintItems,
    photo_download_items: photoDownloadItems,
    digital_bundle_items: digitalBundleItems,
    print_order_service_items: printOrderServiceItems,
  }),
);

/**
 * Checks if the cart requires a shipping address.
 */
export const checkoutCartRequiresAddress = createSelector(
  getAlbumCustomPrintCartItems,
  getAlbumPrintPackageCartItems,
  getPurchasedPrintPackageCartItems,
  getIndividualPrintCartItems,
  getLegacyPrintCartItems,
  getPrintProductCartItems,
  getPhotoPricingPrintProductCartItems,
  (
    albumCustomPrintCartItems,
    albumPrintPackageCartItems,
    purchasedPrintPackageCartItems,
    individualPrintCartItems,
    legacyPrintCartItems,
    printProductCartItems,
    photoPricingPrintProductItems,
  ) => {
    const hasShippableAlbumPrintPackages = albumPrintPackageCartItems.some(
      item => albumPrintPackageRequiresShipping(item.albumPrintPackage),
    );
    const hasShippablePurchasedPrintPackages =
      purchasedPrintPackageCartItems.some(item =>
        albumPrintPackageRequiresShipping(item.purchasedPrintPackage),
      );

    if (
      hasShippableAlbumPrintPackages ||
      hasShippablePurchasedPrintPackages ||
      photoPricingPrintProductItems.length > 0 ||
      individualPrintCartItems.length > 0 ||
      legacyPrintCartItems.length > 0 ||
      printProductCartItems.length > 0
    ) {
      return true;
    }

    return albumCustomPrintCartItems.some(({ albumCustomPrint, shipping }) => {
      return (
        shipping || albumCustomPrintRequiresAddressToConfig(albumCustomPrint)
      );
    });
  },
);

/**
 * Checks if the cart requires showing the Pre-paid notice
 */
export const checkoutCartRequiresPrepaidNotice = createSelector(
  getAlbumPrintPackageCartItems,
  getPhotoPricingPrintProductCartItems,
  getAlbumCustomPrintCartItems,
  (
    albumPrintPackageCartItems,
    photoPricingPrintProductItems,
    albumCustomPrintItems,
  ) => {
    const hasPrepaidPrintPackage = albumPrintPackageCartItems.some(
      item => item.prePaid,
    );
    const hasPrepaidPhotoPricingPrintProduct =
      photoPricingPrintProductItems.some(item => item.prePaid);

    const hasPrepaidCustomPrints = albumCustomPrintItems.some(
      item => item.prePaid,
    );

    if (
      hasPrepaidPrintPackage ||
      hasPrepaidPhotoPricingPrintProduct ||
      hasPrepaidCustomPrints
    ) {
      return true;
    }

    return false;
  },
);

/**
 * Client side pricing.
 */
export const getCheckoutCartTotalPrice = createSelector(getCheckoutCart, cart =>
  cart.reduce(
    (sum, item) => {
      const price = getCheckoutCartItemPrice(item);

      return {
        value: price.value + sum.value,
        currency: price.currency,
      };
    },
    { value: 0, currency: CURRENCY.USD },
  ),
);

/**
 * Returns the AlbumPrintPackage cart items that contains optional
 * extended print package items without a photo (i.e. not configured).
 */
export const getUnconfiguredTaggedAlbumPrintPackageCartItems = createSelector(
  getAlbumPrintPackageCartItems,
  albumPrintPackageCartItems => {
    return albumPrintPackageCartItems.filter(cartItem => {
      // Since it's possible an overridable item has multiple albums / folders tied to it
      // we need to make sure we are looking for overridable in which we have a record
      const printPackageItemWithOverrides =
        cartItem.albumPrintPackage.print_package.items.records.find(
          printPackageItem =>
            printPackageItem.album_folder_print_package_items?.total > 0 &&
            !!(
              printPackageItem.album_folder_print_package_items?.records ?? []
            ).find(
              item =>
                item.album_folder_print_package.album_folder.id ===
                cartItem.albumPrintPackage.album_folder?.id,
            ),
        );

      if (!printPackageItemWithOverrides) {
        return false;
      }

      return !cartItem.extendedPrintPackageItems?.find(
        item => item.printPackageItem.id === printPackageItemWithOverrides.id,
      );
    });
  },
);

/**
 * Returns the AlbumPrintPackage cart items that require
 * print products configurations..
 */
export const getUnconfiguredAlbumPrintPackageCartItems = createSelector(
  getAlbumPrintPackageCartItems,
  albumPrintPackageCartItems => {
    return albumPrintPackageCartItems.filter(cartItem => {
      return (
        !cartItem.prePaid &&
        getPrintPackageCartItemNonConfiguredItems(cartItem)?.length > 0
      );
    });
  },
);

/**
 * Returns the PurchasedPrintPackage cart items that require
 * print products configurations..
 */
export const getUnconfiguredPurchasedPrintPackageCartItems = createSelector(
  getPurchasedPrintPackageCartItems,
  purchasedPrintPackageCartItems => {
    return purchasedPrintPackageCartItems.filter(cartItem => {
      return getPrintPackageCartItemNonConfiguredItems(cartItem)?.length > 0;
    });
  },
);

/**
 * Determines if the cart has a print package which includes a download
 * for the photo provided.
 */
export const hasPrintPackageWithDownloadsIncluded = createSelector(
  getAlbumPrintPackageCartItems,
  selectByPhoto,
  (cart, photo) => {
    if (!photo) {
      return false;
    }
    const packageWithDownloads = cart.filter(
      item =>
        item.type === CART_ITEM.ALBUM_PRINT_PACKAGE &&
        item.photo?.id === photo.id &&
        item.albumPrintPackage?.print_package?.digital_downloads_mode ===
          DIGITAL_DOWNLOADS_MODE.INCLUDED,
    );

    return packageWithDownloads.length > 0;
  },
);
